import { IReadonlyObservableArray } from "azure-devops-ui/Core/Observable";
import React from "react";
import { ISettingsContext } from "../../common/contexts/settings";
import { noop } from "../../common/utilities/func";

export interface ISearchSetting {
  recentSearches?: string[];
}

export interface ISearchContext {
  addSearch: (searchText: string) => void;
  clearSearches: () => void;
  getSearches(): Promise<string[]>;
  removeSearch: (searchText: string) => void;
}

export const SearchContext = React.createContext<ISearchContext>({
  addSearch: noop,
  clearSearches: noop,
  getSearches: () => Promise.resolve([]),
  removeSearch: noop
});

export class PhotoSearchContext implements ISearchContext {
  private searches: string[] = [];
  private settingsContext: ISettingsContext;
  private updateSearches: (searches: string[]) => Promise<void>;

  /**
   * Creates a new instance of the PhotoSearchContext.
   *
   * @param settingsContext The settings context to use for storing recent searches.
   * @param recentSearches The observable array of recent searches.
   * @param updateSearches The callback to update the recent searches.
   */
  constructor(
    settingsContext: ISettingsContext,
    recentSearches: IReadonlyObservableArray<string>,
    updateSearches: (searches: string[]) => Promise<void>
  ) {
    const searchSetting = settingsContext.getSetting<ISearchSetting>("searchSetting");

    // Go through the stored search setting and load up any recent searches.
    // Ensure the data matches our required schema for each of the values we
    // process. This will help with poorly formatted data.
    if (searchSetting) {
      const { recentSearches } = searchSetting;
      if (recentSearches && Array.isArray(recentSearches)) {
        for (let index = 0; index < recentSearches.length; index++) {
          const recentSearch = recentSearches[index];
          if (typeof recentSearch === "string") {
            this.searches.push(recentSearch);
          }
        }
      }
    }

    // Subscribe to any changes to the recent searches and update our copies in
    // memory and in the settings context.
    recentSearches.subscribe(() => {
      this.searches = recentSearches.value;
      this.updateSetting();
    });

    this.settingsContext = settingsContext;
    this.updateSearches = updateSearches;
  }

  /**
   * Adds a search to the searches array. If the search already exists, it will
   * be removed and added to the top of the list. If the list is longer than 6
   * items, the last item will be removed.
   *
   * @param searchText The search to be added.
   */
  public addSearch(searchText: string) {
    // Do a case insensitive comparison to existing searches and remove any
    // duplicate that exist.
    for (let searchIndex = 0; searchIndex < this.searches.length; searchIndex++) {
      if (searchText.localeCompare(this.searches[searchIndex], undefined, { sensitivity: "base" }) === 0) {
        this.searches.splice(searchIndex, 1);
        break;
      }
    }

    // Prepend the new search and ensure we only track the latest 6.
    this.searches.unshift(searchText);
    this.searches.splice(6, 1);
    this.storeSearch();
  }

  /**
   * Clears the recent searches array
   */
  public clearSearches() {
    this.searches.splice(0, this.searches.length);
    this.storeSearch();
  }

  /**
   * Returns the searches array.
   *
   * @return A promise to the string[] of searches.
   */
  public getSearches(): Promise<string[]> {
    return Promise.resolve(this.searches);
  }

  /**
   * Removes a specified search from the searches array.
   *
   * @param searchText The search to remove.
   */
  public removeSearch(searchText: string) {
    const searchIndex = this.searches.indexOf(searchText);

    if (searchIndex !== -1) {
      this.searches.splice(searchIndex, 1);
      this.storeSearch();
    }
  }

  private storeSearch() {
    this.updateSetting();
    this.updateSearches(this.searches);
  }

  private updateSetting() {
    this.settingsContext.mergeSetting("searchSetting", { recentSearches: this.searches });
  }
}
